import { Link, redirect, useFetcher, useLoaderData, type LoaderFunctionArgs } from 'react-router';
import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  EmptyState,
  KbSeverityBadge,
  StatusBadge,
  toast,
} from '@repo-manager/ui';

import { getApi } from '../lib/api';
import { useRepos } from '../lib/repo-context';
import { getSupabase } from '../lib/supabase';

interface EpicRow {
  id: string;
  feature_id: string;
  repo_id: string;
  title: string;
  rationale: string;
  status: string;
  kb_refs: string[];
  approved_at: string | null;
  approved_by: string | null;
}

interface StoryRow {
  id: string;
  title: string;
  status: string;
  effort: string;
}

interface KbRef {
  id: string;
  slug: string;
  title: string;
  type: string;
  severity: 'standard' | 'recommended' | 'optional';
  summary: string;
}

interface LoaderData {
  epic: EpicRow;
  stories: StoryRow[];
  kbRefs: KbRef[];
}

export async function epicLoader({ params }: LoaderFunctionArgs): Promise<LoaderData> {
  if (!params.id) throw new Response('Not Found', { status: 404 });
  const supabase = getSupabase();
  const { data: epic, error } = await supabase
    .from('epics')
    .select('id, feature_id, repo_id, title, rationale, status, kb_refs, approved_at, approved_by')
    .eq('id', params.id)
    .single();
  if (error || !epic) throw new Response('Epic not found', { status: 404 });
  const [storiesR, kbR] = await Promise.all([
    supabase
      .from('stories')
      .select('id, title, status, effort')
      .eq('epic_id', epic.id)
      .order('created_at', { ascending: true }),
    epic.kb_refs.length > 0
      ? supabase
          .from('kb_entries')
          .select('id, slug, title, type, severity, summary')
          .in('slug', epic.kb_refs)
      : Promise.resolve({ data: [] }),
  ]);
  return {
    epic,
    stories: storiesR.data ?? [],
    kbRefs: (kbR.data ?? []) as KbRef[],
  };
}

export async function epicAction({ params, request }: LoaderFunctionArgs) {
  if (!params.id) return null;
  const fd = await request.formData();
  const op = fd.get('op');
  const supabase = getSupabase();

  const { data: epic } = await supabase
    .from('epics')
    .select('id, repo_id, status')
    .eq('id', params.id)
    .single();
  if (!epic) return { ok: false, error: 'Epic not found' };

  if (op === 'approve') {
    try {
      await getApi().triggerApproveEpic({ epic_id: epic.id, repo_id: epic.repo_id });
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : 'trigger failed' };
    }
    const { error: upd } = await supabase
      .from('epics')
      .update({ status: 'approved', approved_at: new Date().toISOString() })
      .eq('id', epic.id);
    if (upd) return { ok: false, error: upd.message };
    return { ok: true, action: 'approve' };
  }
  if (op === 'delete') {
    const { error } = await supabase.from('epics').delete().eq('id', epic.id);
    if (error) return { ok: false, error: error.message };
    return redirect('/roadmap');
  }
  return null;
}

export function EpicPage() {
  const { epic, stories, kbRefs } = useLoaderData() as LoaderData;
  const { repos } = useRepos();
  const repo = repos.find((r) => r.id === epic.repo_id) ?? null;
  const fetcher = useFetcher();
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const r = fetcher.data;
    if (r && typeof r === 'object' && 'ok' in r) {
      const ok = (r as { ok: boolean }).ok;
      if (ok && (r as { action?: string }).action === 'approve') {
        toast.success('Epic approved');
        setConfirmOpen(false);
      } else if (!ok) {
        toast.error('Action failed', { description: (r as { error?: string }).error });
      }
    }
  }, [fetcher.data]);

  const canApprove = epic.status === 'proposed';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          {repo ? (
            <p className="text-sm font-mono text-muted-foreground">
              {repo.owner}/{repo.name}
            </p>
          ) : null}
          <h1 className="text-2xl font-semibold tracking-tight">{epic.title}</h1>
          <div className="mt-1 flex items-center gap-2">
            <StatusBadge status={epic.status} />
            {epic.approved_at ? (
              <span className="text-sm text-muted-foreground">
                approved {new Date(epic.approved_at).toLocaleString()}
              </span>
            ) : null}
          </div>
        </div>
        <Button asChild variant="ghost">
          <Link to={`/features/${epic.feature_id}`}>← Feature</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Rationale</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="whitespace-pre-wrap text-sm">{epic.rationale}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Applicable standards</CardTitle>
          <CardDescription>KB entries the manager picked as relevant context.</CardDescription>
        </CardHeader>
        <CardContent>
          {kbRefs.length === 0 ? (
            <p className="text-sm italic text-muted-foreground">No KB refs listed.</p>
          ) : (
            <ul className="space-y-3">
              {kbRefs.map((k) => (
                <li key={k.id} className="rounded-md border border-border p-3">
                  <div className="flex items-center justify-between">
                    <Link to={`/knowledge/${k.slug}`} className="font-medium hover:underline">
                      {k.title}
                    </Link>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{k.type}</Badge>
                      <KbSeverityBadge severity={k.severity} />
                    </div>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{k.summary}</p>
                </li>
              ))}
            </ul>
          )}
          {epic.kb_refs.length > kbRefs.length ? (
            <p className="mt-2 text-xs text-amber-200">
              {epic.kb_refs.length - kbRefs.length} ref(s) didn&apos;t resolve to a kb_entries row.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Stories ({stories.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {stories.length === 0 ? (
            <EmptyState
              title="No stories"
              description="Stories are proposed under an epic once it's approved."
            />
          ) : (
            <ul className="divide-y divide-border">
              {stories.map((s) => (
                <li key={s.id} className="flex items-center justify-between py-2 text-sm">
                  <span>{s.title}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{s.effort}</Badge>
                    <StatusBadge status={s.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {canApprove ? (
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogTrigger asChild>
            <Button>Approve epic</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Approve this epic?</DialogTitle>
              <DialogDescription>
                Fires <code>POST /api/trigger/approve-epic</code> and flips <code>epics.status</code> to{' '}
                <code>approved</code>. The manager DO picks it up on its next cycle and writes the story
                rows under this epic via <code>supabase_propose_stories</code> — usually within a minute.
                Refresh to see them appear.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <fetcher.Form method="post">
                <input type="hidden" name="op" value="approve" />
                <Button type="submit" disabled={fetcher.state !== 'idle'}>
                  Approve
                </Button>
              </fetcher.Form>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}
